import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";

import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { PageHeader, StatCard, StatusBadge, EmptyState, MainContainer, Section } from "../../components/ui";
import { usePanelStyles } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { canManageTenants, isResellerAdmin } from "../../utils/tenant";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
    },
    page: {
      display: "flex",
      flexDirection: "column",
      gap: 20,
      maxWidth: 1280,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingBottom: theme.spacing(2),
    },
    pageHeader: { paddingTop: 0, paddingBottom: 0 },
    monthPicker: {
      display: "inline-flex",
      alignItems: "center",
      gap: 2,
      height: 40,
      boxSizing: "border-box",
      background: t.paper,
      border: `1px solid ${t.inputBorder}`,
      borderRadius: t.radius.sm,
      padding: "0 4px",
      "&:focus-within": { borderColor: t.brand, boxShadow: t.shadow.glow },
    },
    monthInput: {
      border: "none",
      background: "transparent",
      fontFamily: t.font,
      fontSize: 14,
      fontWeight: 500,
      color: t.text,
      // o seletor nativo acompanha o tema (ícone do calendário no escuro)
      colorScheme: t.mode,
      padding: "6px 4px",
      outline: "none",
      minWidth: 140,
      textAlign: "center",
    },
    monthBtn: { color: t.textSecondary, padding: 6 },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 16,
    },
    sectionTitle: { display: "inline-flex", alignItems: "center", gap: 8 },
    sectionIcon: { fontSize: 18, color: t.textSecondary },
    sectionIconDown: { fontSize: 18, color: t.status.down.fg },
    sectionHint: { fontFamily: t.font, fontSize: 12, color: t.textSecondary },
    // tabela larga rola dentro do cartão
    tableWrap: {
      width: "100%",
      overflowX: "auto",
      ...theme.scrollbarStyles,
      "& .MuiTableCell-root": { whiteSpace: "nowrap" },
      "& .MuiTableRow-root:last-child > .MuiTableCell-body": { borderBottom: 0 },
    },
    emptyCell: { padding: 0, borderBottom: "none" },
  };
});

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (d) => (d ? moment(d).format("DD/MM/YYYY") : "-");

const INVOICE_TONE = { open: "warning", paid: "success", overdue: "error" };

const resolveInvoiceStatus = (row) => {
  const inv = row && row.lastInvoice;
  if (!inv) return null;
  if (inv.status === "paid") return "paid";
  const overdue =
    (row.overdueDays && row.overdueDays > 0) ||
    (inv.dueDate && moment(inv.dueDate).isBefore(moment(), "day"));
  return overdue ? "overdue" : "open";
};

const ResellerFinance = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const allowed = canManageTenants(user);
  const isReseller = isResellerAdmin(user);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reseller/finance/summary", { params: { month } });
      setSummary(data || null);
    } catch (err) {
      toastError(err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    if (!allowed) return;
    fetchSummary();
  }, [allowed, fetchSummary]);

  if (!user || !user.id) return null;
  if (!allowed) return <ForbiddenPage />;

  const shiftMonth = (delta) => setMonth(moment(month, "YYYY-MM").add(delta, "month").format("YYYY-MM"));
  const handleMonthInput = (e) => {
    const v = e.target.value;
    if (/^\d{4}-\d{2}$/.test(v)) setMonth(v);
  };

  const receivable = (summary && summary.receivable) || {};
  const payable = summary && summary.payable;
  const byPlan = (summary && summary.byPlan) || [];
  const byCompany = (summary && summary.byCompany) || [];
  const delinquent = (summary && summary.delinquent) || [];
  const overdueValue = Number(receivable.overdue || 0);
  const monthLabel = moment(month, "YYYY-MM").format("MMMM YYYY");

  const goInvoices = (companyId) => history.push(`/financeiro?companyId=${companyId}`);
  const goCompany = () => history.push("/settings?tab=companies");

  const rowActions = (companyId) => (
    <span className={panel.rowActions}>
      <Tooltip title={i18n.t("resellerFinance.actions.invoices")}>
        <IconButton onClick={() => goInvoices(companyId)} aria-label={i18n.t("resellerFinance.actions.invoices")}>
          <ReceiptOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={i18n.t("resellerFinance.actions.company")}>
        <IconButton onClick={goCompany} aria-label={i18n.t("resellerFinance.actions.company")}>
          <BusinessIcon />
        </IconButton>
      </Tooltip>
    </span>
  );

  const payableHint = payable
    ? `${i18n.t("resellerFinance.stats.dueOn")} ${fmtDate(payable.dueDate)} · ${i18n.t(
        `resellerFinance.status.${payable.status === "paid" ? "paid" : payable.status === "overdue" ? "overdue" : "open"}`
      )}`
    : i18n.t("resellerFinance.stats.noPayable");

  return (
    <MainContainer>
      <div className={classes.scroll}>
        <div className={classes.page}>
          <PageHeader
            className={classes.pageHeader}
            title={`${i18n.t("resellerFinance.title")} — ${monthLabel}`}
            subtitle={
              user.super
                ? i18n.t("resellerFinance.subtitlePlatform")
                : i18n.t("resellerFinance.subtitleReseller")
            }
            actions={
              <div className={classes.monthPicker}>
                <IconButton size="small" className={classes.monthBtn} onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <input
                  type="month"
                  className={classes.monthInput}
                  value={month}
                  onChange={handleMonthInput}
                  aria-label={i18n.t("resellerFinance.month")}
                />
                <IconButton size="small" className={classes.monthBtn} onClick={() => shiftMonth(1)} aria-label="Próximo mês">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </div>
            }
          />

          {/* KPIs */}
          <div className={classes.statGrid}>
            <StatCard
              label={i18n.t("resellerFinance.stats.receivable")}
              value={fmtMoney(receivable.open)}
              icon={<HourglassEmptyIcon />}
              tone="warning"
              hint={`${receivable.count || 0} ${i18n.t("resellerFinance.stats.invoices")} · ${receivable.companies || 0} ${i18n.t("resellerFinance.stats.companies")}`}
            />
            <StatCard
              label={i18n.t("resellerFinance.stats.paid")}
              value={fmtMoney(receivable.paid)}
              icon={<CheckCircleOutlineIcon />}
              tone="success"
            />
            <StatCard
              label={i18n.t("resellerFinance.stats.overdue")}
              value={fmtMoney(overdueValue)}
              icon={<WarningRoundedIcon />}
              tone={overdueValue > 0 ? "error" : "neutral"}
              hint={
                delinquent.length > 0
                  ? `${delinquent.length} ${i18n.t("resellerFinance.stats.delinquentCompanies")}`
                  : i18n.t("resellerFinance.stats.noOverdue")
              }
            />
            <StatCard
              label={i18n.t("resellerFinance.stats.mrr")}
              value={fmtMoney(summary && summary.mrr)}
              icon={<TrendingUpIcon />}
              tone="brand"
              hint={i18n.t("resellerFinance.stats.mrrHint")}
            />
            {isReseller && (
              <StatCard
                label={i18n.t("resellerFinance.stats.payable")}
                value={payable ? fmtMoney(payable.value) : "—"}
                icon={<AccountBalanceOutlinedIcon />}
                tone={payable && payable.status === "overdue" ? "error" : "info"}
                hint={payable && payable.breakdown ? payable.breakdown : payableHint}
                onClick={() => history.push("/financeiro")}
              />
            )}
          </div>

          {/* INADIMPLENTES */}
          {delinquent.length > 0 && (
            <Section
              flush
              title={
                <span className={classes.sectionTitle}>
                  <WarningRoundedIcon className={classes.sectionIconDown} />
                  {i18n.t("resellerFinance.delinquent.title")}
                  <StatusBadge color="error" size="sm" label={`${delinquent.length}`} dot={false} />
                </span>
              }
              actions={<span className={classes.sectionHint}>{i18n.t("resellerFinance.delinquent.hint")}</span>}
            >
              <div className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("resellerFinance.table.company")}</TableCell>
                      <TableCell>{i18n.t("resellerFinance.table.overdueDays")}</TableCell>
                      <TableCell align="right">{i18n.t("resellerFinance.table.value")}</TableCell>
                      <TableCell align="right">{i18n.t("resellerFinance.table.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {delinquent.map((d) => (
                      <TableRow key={d.companyId}>
                        <TableCell className={panel.cellStrong}>{d.name || `#${d.companyId}`}</TableCell>
                        <TableCell>
                          <StatusBadge color="error" size="sm" label={`${d.overdueDays || 0} ${i18n.t("resellerFinance.table.days")}`} />
                        </TableCell>
                        <TableCell align="right" className={panel.cellStrong}>{fmtMoney(d.value)}</TableCell>
                        <TableCell align="right">{rowActions(d.companyId)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Section>
          )}

          {/* POR EMPRESA */}
          <Section
            flush
            title={
              <span className={classes.sectionTitle}>
                <BusinessIcon className={classes.sectionIcon} />
                {i18n.t("resellerFinance.byCompany.title")}
                <StatusBadge color="neutral" size="sm" label={`${byCompany.length}`} dot={false} />
              </span>
            }
          >
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("resellerFinance.table.company")}</TableCell>
                    <TableCell>{i18n.t("resellerFinance.table.plan")}</TableCell>
                    <TableCell align="right">{i18n.t("resellerFinance.table.value")}</TableCell>
                    <TableCell>{i18n.t("resellerFinance.table.dueDate")}</TableCell>
                    <TableCell>{i18n.t("resellerFinance.table.lastInvoice")}</TableCell>
                    <TableCell align="right">{i18n.t("resellerFinance.table.overdueDays")}</TableCell>
                    <TableCell align="right">{i18n.t("resellerFinance.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableRowSkeleton columns={7} />}
                  {!loading &&
                    byCompany.map((row) => {
                      const st = resolveInvoiceStatus(row);
                      const inv = row.lastInvoice;
                      const value = row.amount != null ? row.amount : inv ? inv.value : null;
                      return (
                        <TableRow key={row.companyId}>
                          <TableCell className={panel.cellStrong}>
                            {row.name || `#${row.companyId}`}
                            {row.status === false && (
                              <>
                                {" "}
                                <StatusBadge color="neutral" size="sm" label={i18n.t("resellerFinance.table.inactive")} dot={false} />
                              </>
                            )}
                          </TableCell>
                          <TableCell>{row.plan || "-"}</TableCell>
                          <TableCell align="right">{value != null ? fmtMoney(value) : "-"}</TableCell>
                          <TableCell>{fmtDate(row.dueDate)}</TableCell>
                          <TableCell>
                            {st ? (
                              <Tooltip title={inv ? `${fmtMoney(inv.value)} · ${fmtDate(inv.dueDate)}` : ""}>
                                <span>
                                  <StatusBadge color={INVOICE_TONE[st]} size="sm" label={i18n.t(`resellerFinance.status.${st}`)} />
                                </span>
                              </Tooltip>
                            ) : (
                              <StatusBadge color="neutral" size="sm" label={i18n.t("resellerFinance.status.none")} dot={false} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {row.overdueDays > 0 ? (
                              <span className={panel.cellStrong}>{row.overdueDays}</span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="right">{rowActions(row.companyId)}</TableCell>
                        </TableRow>
                      );
                    })}
                  {!loading && byCompany.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className={classes.emptyCell}>
                        <EmptyState
                          icon={<BusinessIcon />}
                          title={i18n.t("resellerFinance.empty.title")}
                          message={i18n.t("resellerFinance.empty.message")}
                          action={
                            <Button variant="contained" color="primary" onClick={goCompany}>
                              {i18n.t("resellerFinance.empty.action")}
                            </Button>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Section>

          {/* POR PLANO */}
          {byPlan.length > 0 && (
            <Section
              flush
              title={
                <span className={classes.sectionTitle}>
                  <TrendingUpIcon className={classes.sectionIcon} />
                  {i18n.t("resellerFinance.byPlan.title")}
                </span>
              }
              actions={<span className={classes.sectionHint}>{i18n.t("resellerFinance.byPlan.hint")}</span>}
            >
              <div className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("resellerFinance.table.plan")}</TableCell>
                      <TableCell align="right">{i18n.t("resellerFinance.table.companies")}</TableCell>
                      <TableCell align="right">{i18n.t("resellerFinance.table.amount")}</TableCell>
                      <TableCell align="right">{i18n.t("resellerFinance.table.mrr")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byPlan.map((p) => (
                      <TableRow key={p.planId}>
                        <TableCell className={panel.cellStrong}>{p.name}</TableCell>
                        <TableCell align="right">{p.companies}</TableCell>
                        <TableCell align="right">{fmtMoney(p.amount)}</TableCell>
                        <TableCell align="right" className={panel.cellStrong}>{fmtMoney(p.mrr)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Section>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default ResellerFinance;
